import React, { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

export default function RateSellerDialog({
    open,
    onOpenChange,
    userId,
    listingId,
    conversationId,
    userName,
    onRated,
}) {
    const [stars, setStars] = useState(5);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit() {
        setBusy(true);
        try {
            await api.post(`/users/${userId}/ratings`, {
                stars,
                comment,
                listing_id: listingId || null,
                conversation_id: conversationId || null,
            });
            toast.success("Rating submitted. Thanks for keeping the community honest.");
            onOpenChange(false);
            onRated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="surface border-onyx-600 rounded-md max-w-md"
                data-testid="rate-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="headline text-2xl">
                        Rate {userName || "seller"}
                    </DialogTitle>
                </DialogHeader>
                <div>
                    <div className="label-caps mb-2">Your rating</div>
                    <div
                        className="flex items-center gap-1"
                        onMouseLeave={() => setHover(0)}
                        data-testid="star-picker"
                    >
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                onMouseEnter={() => setHover(n)}
                                onClick={() => setStars(n)}
                                className="p-1"
                                data-testid={`star-${n}`}
                                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            >
                                <Star
                                    className={`w-8 h-8 ${
                                        (hover || stars) >= n
                                            ? "fill-hellcat text-hellcat"
                                            : "text-onyx-600"
                                    }`}
                                    strokeWidth={1.5}
                                />
                            </button>
                        ))}
                        <span className="ml-2 text-sm text-onyx-500">
                            {stars} / 5
                        </span>
                    </div>
                    <label className="flex flex-col gap-1.5 mt-4">
                        <span className="label-caps">Comment (optional)</span>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            data-testid="rate-comment"
                            maxLength={800}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat h-24"
                            placeholder="How was the transaction? Communication? Car as described?"
                        />
                    </label>
                </div>
                <DialogFooter>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="rate-submit"
                        className="btn-red rounded-sm px-4 py-2.5 disabled:opacity-60"
                    >
                        {busy ? "Saving..." : "Submit rating"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
